/**
 * Email verification: waiting, resending, and confirming from a link.
 *
 * The rule that shapes this screen: **a key in the URL is never proof.** A
 * screen that renders "Verified" because `/auth/verify-email/:key` matched would
 * be confidently wrong for an expired key, a reused key, a key someone typed, or
 * a key from a different account. It would also be trivially forgeable by
 * anybody who can edit an address bar.
 *
 * So arriving with a key produces exactly one thing: a submission. The server
 * decides, and only its answer moves this screen to a confirmed state. Arriving
 * without one produces the waiting state instead.
 */

import { useEffect, useRef, useState } from 'react';
import { Button } from '../design/Button';
import { TextField } from '../design/Field';
import { Alert, AuthLayout } from '../design/Feedback';
import { FailureSummary, SuccessPanel, describeFailure, useSubmission } from './formSupport';
import { useAuth } from './AuthProvider';
import { forgetKeyInAddressBar, type Route } from './routes';
import type { AuthFailure } from './formSupport';

export interface VerifyEmailProps {
  route: Extract<Route, { name: 'verify-email' }>;
  navigate: (route: Route, options?: { replace?: boolean }) => void;
}

type Phase = 'waiting' | 'confirming' | 'confirmed' | 'failed';

export function VerifyEmail({ route, navigate }: VerifyEmailProps) {
  const { auth, state, refresh } = useAuth();
  const [phase, setPhase] = useState<Phase>(route.key ? 'confirming' : 'waiting');
  const [keyFailure, setKeyFailure] = useState<AuthFailure | null>(null);
  const [email, setEmail] = useState(state.pendingEmail ?? state.user?.email ?? '');
  const [resent, setResent] = useState(false);
  const { busy, failure, submit } = useSubmission();
  const attempted = useRef(false);

  // Submit the key exactly once. Without the guard, React's development double
  // render would spend a single-use key on the first paint and the second
  // attempt would legitimately fail.
  useEffect(() => {
    if (!route.key || attempted.current) return;
    attempted.current = true;

    void (async () => {
      try {
        await auth.verifyEmail(route.key as string);
        setPhase('confirmed');
        await refresh();
      } catch (error) {
        setKeyFailure(describeFailure(error));
        setPhase('failed');
      } finally {
        // Spent or not, the key does not belong in the address bar, the back
        // button or a shared screenshot.
        forgetKeyInAddressBar(route);
      }
    })();
  }, [route, auth, refresh]);

  if (phase === 'confirming') {
    return (
      <AuthLayout heading="Confirming your email">
        {/* Honest present tense: the server has not answered yet. */}
        <p className="mm-auth__note" role="status">
          Checking your confirmation link…
        </p>
      </AuthLayout>
    );
  }

  if (phase === 'confirmed') {
    return (
      <AuthLayout heading="Email confirmed">
        <SuccessPanel
          title="You are all set"
          actions={
            <Button variant="primary" size="lg" onClick={() => navigate({ name: 'app', destination: 'home' }, { replace: true })}>
              Start writing
            </Button>
          }
        >
          <p>Your email address is confirmed. Your account is ready to use.</p>
        </SuccessPanel>
      </AuthLayout>
    );
  }

  const resend = async () => {
    const ok = await submit(async () => {
      await auth.resendVerificationEmail(email.trim());
    });
    if (ok) setResent(true);
  };

  return (
    <AuthLayout
      heading={phase === 'failed' ? 'That link did not work' : 'Confirm your email'}
      sub={
        phase === 'failed'
          ? 'Confirmation links expire, and each one can only be used once.'
          : undefined
      }
    >
      {phase === 'failed' ? <FailureSummary failure={keyFailure} /> : null}
      <FailureSummary failure={failure} />

      {phase === 'waiting' ? (
        <p className="mm-auth__note">
          {state.pendingEmail || state.user?.email ? (
            <>
              We sent a confirmation link to <strong>{state.pendingEmail ?? state.user?.email}</strong>.
              Open it on this iPad to finish.
            </>
          ) : (
            <>Open the confirmation link we sent you to finish setting up your account.</>
          )}
        </p>
      ) : null}

      {resent ? (
        <Alert tone="success">
          If that address needs confirming, a new link is on its way. It replaces any earlier one.
        </Alert>
      ) : null}

      <form
        onSubmit={(event) => {
          event.preventDefault();
          void resend();
        }}
        noValidate
      >
        <TextField
          label="Email"
          type="email"
          name="email"
          value={email}
          autoComplete="email"
          autoCapitalize="off"
          autoCorrect="off"
          spellCheck={false}
          inputMode="email"
          required
          help="Send the confirmation link again if it did not arrive."
          error={failure?.fields.email}
          onChange={(event) => setEmail(event.target.value)}
        />
        <div className="mm-auth__actions">
          <Button type="submit" variant="secondary" block loading={busy}>
            Send a new link
          </Button>
        </div>
      </form>

      <div className="mm-auth__aside">
        <button type="button" className="mm-auth__link" onClick={() => navigate({ name: 'app', destination: 'home' })}>
          Continue without confirming
        </button>
      </div>
    </AuthLayout>
  );
}
